"use client";

import clsx from "clsx";
import useEmblaCarousel from "embla-carousel-react";
import Image from "next/image";
import React, { useCallback } from "react";

import type { FragmentType } from "../../../../__generated__/gql";
import { graphql, useFragment } from "../../../../__generated__/gql";
import { Markdown } from "../../../_components/ui/markdown/markdown";
import { useResponsiveValue } from "../../../_hooks/use-responsive-value";
import { useDotButton } from "../_hooks/use-dot-button";

import { Divider } from "./divider";

const Fragment = graphql(`
  fragment ContentArchitectureTypeExperiences on ContentArchitectureType {
    experiences {
      name
      imageUrl {
        rawUrl
      }
      descriptionMarkup
    }
  }
`);

export function ArchitectureTypeExperiences({
  contentArchitectureType,
}: {
  contentArchitectureType: FragmentType<typeof Fragment>;
}) {
  const experiences = useFragment(
    Fragment,
    contentArchitectureType,
  ).experiences;
  const isCarouselActive = useResponsiveValue({
    base: experiences.length > 1,
    lg: experiences.length > 3,
  });
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    active: isCarouselActive,
  });
  const { selectedIndex, scrollSnaps, onDotButtonClick } =
    useDotButton(emblaApi);
  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);
  if (experiences.length === 0) return null;
  return (
    <React.Fragment>
      <Divider />
      <div className="flex w-[calc(100%+80px)] translate-x-[-40px] gap-[10px]">
        <div className="flex max-h-[480px] w-[30px] items-center justify-center">
          <button
            // カルーセルの送りだけで、行動の分析対象にならない
            data-analytics-ignore
            className={clsx("h-[30px] w-[30px]", {
              hidden: !isCarouselActive,
            })}
            onClick={scrollPrev}
          >
            <Image
              src="/i/icon_chevron_left.svg"
              alt="前の体験へ"
              width={30}
              height={30}
            />
          </button>
        </div>
        <div className="w-[calc(100%-80px)] shrink-0 overflow-hidden">
          <div ref={emblaRef}>
            <div className="flex">
              {experiences.map((experience, i) => (
                <div
                  key={i}
                  className="bg-slightbeige-108 w-full min-w-0 flex-[0_0_100%] lg:mr-[3%] lg:flex-[0_0_calc(94%/3)]"
                >
                  <div className="flex flex-col gap-[20px]">
                    <div className="aspect-3/2 w-full">
                      <Image
                        src={experience.imageUrl.rawUrl}
                        alt={`「${experience.name}」の画像`}
                        sizes="100vw"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                        width={3}
                        height={2}
                      />
                    </div>
                    <div className="flex flex-col items-center gap-[10px] pb-[30px]">
                      <div className="w-[calc(100%-40px)] text-[14px] md:text-[16px]">
                        {experience.name}
                      </div>
                      <div className="w-[calc(100%-40px)] text-[10px] leading-[180%] md:text-[13px]">
                        <Markdown>{experience.descriptionMarkup}</Markdown>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div
            className={clsx(
              "flex h-[30px] items-center justify-center gap-[10px] md:mt-[10px]",
              {
                hidden: !isCarouselActive,
              },
            )}
          >
            {scrollSnaps.map((_, index) => (
              <button
                key={index}
                // カルーセルの送りだけで、行動の分析対象にならない
                data-analytics-ignore
                onClick={() => onDotButtonClick(index)}
                className={clsx(
                  "bg-sbeige h-[5px] w-[5px] rounded-full",
                  selectedIndex === index && "bg-skogecha",
                )}
              ></button>
            ))}
          </div>
        </div>
        <div className="flex max-h-[480px] w-[30px] items-center justify-center">
          <button
            // カルーセルの送りだけで、行動の分析対象にならない
            data-analytics-ignore
            className={clsx("h-[30px] w-[30px]", {
              hidden: !isCarouselActive,
            })}
            onClick={scrollNext}
          >
            <Image
              src="/i/icon_chevron_left.svg"
              alt="次の画像へ"
              width={30}
              height={30}
              className="rotate-180"
            />
          </button>
        </div>
      </div>
    </React.Fragment>
  );
}
